<template lang="html">
  <section
    class="m-card__layout"
    :id="id"
    :draggable="draggable"
    @dragstart.stop="onComponentStartDrag($event)"
    @dragend.stop="onComponentEndDrag($event)"
    @dragover.prevent.stop
    @dragenter.stop="onComponentDragEnter($event)"
    @drop.prevent.stop
    :class="{ 'm-card__section': setSectionClass }"
  >
    <base-card>
      <template v-slot:header v-if="card">
        <card-layout-header
          :icon="headerIcon"
          :title="headerTitle"
          :action="actionText"
          @on-edit-card-layout="onEditCardLayout"
          @on-delete-card-layout="onDeleteCardLayout"
        />

        <div class="card-description normal-wrap">
          {{ description }}
        </div>
      </template>

      <div
        ref="canvas"
        class="m-card-layout-main"
        :class="{
          'm-card__section': setSectionClass,
          'm-card-flex-row': this.type === LAYOUT_TYPE.ROW,
          'm-card-flex-column': this.type !== LAYOUT_TYPE.ROW
        }"
      ></div>
      <simple-modal
        :show="displaySimpleModal"
        :message="getMessageText(messages.ARE_YOU_SURE)"
        :cancel-text="getMessageText(messages.CANCEL)"
        :ok-text="getMessageText(messages.OK)"
        @on-simple-modal-cancel="onCancelDeleteElement"
        @on-simple-modal-ok="onConfirmDeleteElement"
      />
    </base-card>
  </section>
</template>

<script lang="js">
  import Vue from "vue";
  import store from "@/store";
  import utils from "@/utils"
  import { MUTATION_NAME } from "@/static/global-variables";
  import { ICONS } from "@/assets/icons/slds-icon-info";
  import { LAYOUT_TYPE } from "@/views/drag-drop-pages/static/layout-type";
  import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";
  import BaseCard from "@/views/drag-drop-pages/components/card/BaseCard";
  import CardLayoutHeader from "@/views/drag-drop-pages/components/CardLayoutHeader";
  import SimpleModal from "@/views/drag-drop-pages/components/modal/SimpleModal";

  export default  {
    name: "card-layout",
    components: {
      BaseCard,
      CardLayoutHeader,
      SimpleModal
    },
    props: {
      // props specific to draggable component
      id: {
        type: String
      },
      allowDropzones: {
        type: Boolean,
        default: true
      },
      dropEffect: {
        type: String,
        default: dragAndDrop.DROP_EFFECT.MOVE
      },
      draggable: {
        type: Boolean,
        default: false
      },
      card: {
        type: Boolean,
        default: true
      },
      type: {
        type: String,
        default: LAYOUT_TYPE.SECTION
      },
      description: {
        type: String
      },
      // props specific to this component below
      headerContent: {
        type: Object,
        default: function() {
          return {
            icon: null,
            expression: {},
            action: null
          };
        }
      }
    },
    computed: {
      LAYOUT_TYPE() {
        return LAYOUT_TYPE
      },
      maxColumnWidth() {
        return this.numColumns ? Math.floor(100 / this.numColumns) : 100;
      },
      messages() {
        return store.state.messages;
      },
      headerIcon() {
        return this.headerContent.icon || {};
      },
      headerTitle() {
        return utils.findExpressionLabel(this.headerContent.expression);
      },
      actionText() {
        if (this.headerContent.action) {
          const action = this.headerContent.action;
          let count = action.navigation.type ? 1 : 0;
          count += action.dataActions? action.dataActions.length : 0;
          return this.replaceMessageText(this.messages.USER_ACTION_COUNT, count);
        }
        return null;
      },
      hasHeader() {
        return Boolean(this.headerIcon || this.headerTitle.length);
      },
      deleteModal: {
        set(data) {
          store.dispatch(MUTATION_NAME.SET_DELETE_DRAGGABLE, data);
        },
        get() {
          return store.getters.deleteDraggableItem;
        }
      },
      editModal: {
        set(data) {
          store.dispatch(MUTATION_NAME.SET_EDIT_DRAGGABLE, data);
        },
        get() {
          return store.getters.editDraggableItem;
        }
      }
    },
    watch: {
      card: function() {
        this.setupSection();
        this.updateRowLayout();
      },
      type: function() {
        this.setupSection();
        this.updateRowLayout();
      }
    },
    mounted () {},
    data () {
      return {
        displaySimpleModal: false,
        setSectionClass: false,
        numColumns: 1
      }
    },
    methods: {
      setupSection() {
        this.setSectionClass = false;
        if(!this.card) {
          this.$nextTick(() => {
            this.setSectionClass = true;
          });
        }
      },
      updateRowLayout() {
        this.numColumns = 1;
        if(this.type === LAYOUT_TYPE.ROW) {
          this.numColumns = this.$refs.canvas.childNodes.length;
        }
      },
      getBody() {
        let body;
        for(const el of this.$refs.canvas.children) {
          const component = this.$refs[el.id];
          if (component instanceof Vue && component.getMetadata) {
            // there should be only one
            let bodyMeta = component.getMetadata();
            if (bodyMeta.type === LAYOUT_TYPE.SECTION && !bodyMeta.element.card) {
              body = bodyMeta.body;
            } else {
              body = bodyMeta;
            }
            break;
          }
        }
        return body;
      },
      getChildrenMetadata() {
        const children = [];
        for(const el of this.$refs.canvas.children) {
          const component = this.$refs[el.id];
          if(component instanceof Vue && component.getMetadata) {
            children.push(component.getMetadata());
          }
        }
        return children;
      },
      getContent(meta) {
        if(this.headerIcon?.name) {
          meta.header = meta.header || {};
          meta.header.icon = {
            category: this.headerIcon.category,
            name: this.headerIcon.name
          };
        }
        if(this.headerContent?.expression &&
          Object.keys(this.headerContent.expression).length) {
          meta.header = meta.header || {};
          meta.header.title = {
            expression: this.headerContent.expression,
            action: this.headerContent.action
          };
        }
        meta.header = meta.header || {};
        meta.header.description = this.description;
        return meta;
      },
      /**
       * @function getMetadata
       * @returns {Object} Metadata in JSON format
      */
     getMetadata() {
        let meta = {
          element: {
            name: this.$options.name,
            card: this.card
          },
          type: this.type
        };
        meta = this.getContent(meta);
        if(this.type === LAYOUT_TYPE.SECTION) {
          const body = this.getBody();
          if(body) {
            meta.body = body;
          }
        } else {
          meta.children = this.getChildrenMetadata();
        }
        return meta;
      },

      destroyChildren() {
        if (this.$refs.canvas && this.$refs.canvas.children) {
          for(const el of this.$refs.canvas.children) {
            const component = this.$refs[el.id];
            if(component instanceof Vue && component.id.indexOf("card-layout-") === 0) {
              component.destroyChildren();
            }
            component.$destroy();
          }
        }
      },

      normalizeElements() {
        for(const el of this.$refs.canvas.children) {
          const component = this.$refs[el.id];
          if(component instanceof Vue && component.id.indexOf("card-layout-") === 0) {
            component.normalizeElements();
          }
        }
        if(!this.card) {
          if(this.$refs.canvas.children.length === 0 && this.$el.id.indexOf("main-layout") !== 0) {
            this.$destroy();
            this.$el.parentNode.removeChild(this.$el);
            return;
          }

          if(this.$refs.canvas.children.length === 1 && (this.type === LAYOUT_TYPE.ROW || this.type === LAYOUT_TYPE.COLUMN)) {
            this.type = LAYOUT_TYPE.SECTION;
          }
        }
        this.updateRowLayout();
      },

      deserialize(serializedJson) {
        this.type = serializedJson.type;
        this.card = serializedJson.element.card !== undefined? serializedJson.element.card : false;
        const header = serializedJson.header;
        const title = header ? header.title : null;
        if (header) {
          if(header.icon) {
            this.headerContent.icon = ICONS.find(i => i.name === header.icon.name);
          }
          this.description = header.description;
        }
        if (title) {
          this.headerContent.expression = title.expression;
          this.headerContent.action = title.action;
        }
        this.description = header ? header.description : null;
      },
      onEditCardLayout() {
        const obj = {
          id: this.id,
          modal: "AddCardModal",
          dropEffect: dragAndDrop.DROP_EFFECT.NONE,
          props: {
            description: this.description,
            headerContent: JSON.parse(JSON.stringify(this.headerContent))
          }
        };
        this.editModal = {detail: obj};
      },
      onCancelDeleteElement() {
        this.displaySimpleModal = false;
      },
      onConfirmDeleteElement() {
        this.displaySimpleModal = false;
        const obj = {
          id: this.id
        };
        this.deleteModal = {detail: obj};
      },
      onDeleteCardLayout() {
        this.displaySimpleModal = true;
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  @include drag-drop-a-tag;

  .m-card-flex-row {
    display: flex;
    flex-direction: row;
    gap: 12px;
  }

  .m-card-flex-column {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .m-card__layout {
    @include mixin-box;
    display: flex;
    flex: 1;
    position: relative;
  }
  .m-card-layout-main {
    min-height: 2rem;
    min-width: 0;
    padding: 10px;
    height: 100%;
    flex: 1;
    justify-items: stretch;
    align-items: stretch;
  }
  .m-card__section {
    flex: 1;
    border: 0;
    box-shadow: none;
    padding: 0;
    margin: 0;
  }
  .m-shrink {
    @include mixin-shrink-layout;
  }

  .card-description {
    padding: 0 0.75rem;
  }
</style>
